import config from '../../config';
import { authHeader } from '../../helpers';
import eventbus from '../../eventbus';
import axios from 'axios';

export const orderService = {
  getCart,
  processCart,
  upsertProduct,
  removeProduct,
  payDonation,
  upsertRegistration,
  removeRegistration,
  getOrders,
  retrieveConferencesToCome
};

/**
 * Retrieve a lost cart, create a new one (if logged-in or not)
 * @returns {Promise<Response>}
 */
function getCart() {
  const requestOptions = {
    method: 'GET',
    headers: authHeader()
  };
  return fetch(`${config.apiUrl}/cart`, requestOptions).then(handleResponse);
}

/**
 * Retrieve the orders
 * @returns {Promise<Response>}
 */
function getOrders() {
  // Retrieve all the orders from Stripe
  const requestOptions = {
    method: 'GET',
    headers: authHeader()
  };
  return fetch(`${config.apiUrl}/orders`, requestOptions).then(handleResponse);
}

/**
 * Process the cart with the given ID.
 * @returns {Promise<Response>}
 */
async function processCart() {
  try {
    const response = await axios.post('/cart/charge', {}, {
      headers: { ...authHeader(), 'Content-Type': 'application/json' }
    });
    return response.data;
  }
  catch (e) {
    if (e.response.status === 401) {
      eventbus.$emit('logout');
    }
    throw e;
  }
}

/**
 * Add / Update qty a product to the cart.
 * @param productId - Product ID
 * @param quantity - Quantity of that product. If 0, product is removed.
 * @returns {Promise<Response>}
 */
function upsertProduct(productId, quantity) {
  const requestOptions = {
    method: 'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      productId, quantity
    })
  };
  return fetch(`${config.apiUrl}/cart/products`, requestOptions).then(handleResponse);
}

/**
 * Remove product from the card.
 * @param productId - Product to remove
 * @returns {Promise<Response>}
 */
function removeProduct(productId) {
  const requestOptions = {
    method: 'DELETE',
    headers: { ...authHeader(), 'Content-Type': 'application/json' }
  };
  return fetch(`${config.apiUrl}/cart/products/${productId}`, requestOptions).then(handleResponse);
}

/**
 * Give a donation.
 *
 * @param amount - Donation amount
 * @param email
 * @param donor
 * @param name
 * @param paymentMethod
 *
 * @returns {Promise<Response>}
 */
function payDonation({ amount, email, donor, name, paymentMethod }) {
  const requestOptions = {
    method: 'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      amount, email, donor, name, paymentMethod
    })
  };
  return fetch(`${config.apiUrl}/donation`, requestOptions).then(handleResponse);
}

/**
 * Add or update an registration to an activity, class or conference. For Member only
 *
 * @param registrationId - Class, Activity or Conference ID
 * @param type - ['class', 'activity', 'conference']
 * @param memberId - Member's ID (not the user id)
 * @returns {Promise<Response>}
 */
function upsertRegistration(registrationId, type, memberId) {
  const requestOptions = {
    method: 'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      registrationId, memberId
    })
  };
  return fetch(`${config.apiUrl}/cart/registration/${type}`, requestOptions).then(handleResponse);
}

/**
 * Remove a registration to an activity, class or conference. For Member only
 *
 * @param registrationId - Class, Activity or Conference ID
 * @param type - ['class', 'activity', 'conference']
 * @param memberId - Member's ID (not the user id)
 * @returns {Promise<Response>}
 */
function removeRegistration(registrationId, type, memberId) {
  const requestOptions = {
    method: 'DELETE',
    headers: { ...authHeader(), 'Content-Type': 'application/json' }
  };
  return fetch(`${config.apiUrl}/cart/registration/${type}/${registrationId}/${memberId}`, requestOptions).then(handleResponse);
}

function retrieveConferencesToCome() {
  const requestOptions = {
    method: 'GET',
    headers: { ...authHeader() }
  };
  return fetch(`${config.apiUrl}/conferences-zooms`, requestOptions).then(handleResponse);
}

function handleResponse(response) {
  return response.text().then(text => {
    const data = text && JSON.parse(text);
    if (!response.ok) {
      if (response.status === 401) {
        eventbus.$emit('logout');
      }

      const error = data ? data.message || response.statusText : response.statusText;
      return Promise.reject(error);
    }

    return data;
  });
}
